import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReportsDashboard from "@/components/ReportsDashboard";
import {
  Calendar,
  Gift,
  Star,
  Clock,
  Scissors,
  LogOut,
  Menu,
  X,
  Plus,
  MessageSquare,
  ChevronRight,
  Sparkles,
  User,
  Repeat,
  Users,
  Heart,
  Share2,
  Trophy,
  HelpCircle,
  Home,
  TrendingUp,
  Upload,
  Receipt,
  AlertCircle,
  CheckCircle,
  DollarSign,
  BookOpen,
  Play,
  Download,
  BarChart3
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import AIChatbot from "@/components/AIChatbot";
import { WriteReviewModal, PendingReviews } from "@/components/ReviewSystem";
import GetStartedGuide, { shouldShowGuide } from "@/components/GetStartedGuide";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import RecurringAppointments from "@/components/RecurringAppointments";
import WaitlistManager from "@/components/WaitlistManager";
import MyFavorites from "@/components/MyFavorites";
import BetaInviteSystem from "@/components/BetaInviteSystem";
import ReferralDashboard from "@/components/ReferralDashboard";
import ProfileDropdown from "@/components/ProfileDropdown";
import MobileBottomNav from "@/components/MobileBottomNav";
import { DashboardSkeleton, StatCardSkeleton } from "@/components/LoadingSkeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PayAtServiceReceiptUpload from "@/components/PayAtServiceReceiptUpload";
import AppointmentDetailModal from "@/components/AppointmentDetailModal";

const ClientDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loyaltyData, setLoyaltyData] = useState({ points: 0, tier: { name: 'Bronze', discount: 5 } });
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  // Removed duplicate showGettingStarted - using OnboardingChecklist instead
  const [favorites, setFavorites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedAppointmentForReview, setSelectedAppointmentForReview] = useState(null);
  const [showGetStartedGuide, setShowGetStartedGuide] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  
  // Appointment detail modal state
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  
  // Payment history state
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({
    total_appointments: 0,
    completed_appointments: 0,
    total_spent: 0,
    total_refunded: 0,
    net_spent: 0
  });
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);
  
  // Receipt upload state
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] = useState(null);
  const [stylistPaymentInfo, setStylistPaymentInfo] = useState(null);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);

  // Fetch stylist payment info when opening receipt upload
  const handleOpenReceiptUpload = async (apt, paymentMethod) => {
    setSelectedAppointmentForPayment({ ...apt, selectedPaymentMethod: paymentMethod });
    setLoadingPaymentInfo(true);
    try {
      const response = await axios.get(`${API}/payments/stylist/${apt.stylist_id}/methods`);
      setStylistPaymentInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch stylist payment info:", error);
      toast.error("Failed to load payment information");
    } finally {
      setLoadingPaymentInfo(false);
      setShowReceiptUpload(true);
    }
  };

  // Get payment status badge
  const getPaymentStatusBadge = (apt) => {
    const status = apt.payment_status;
    const method = apt.payment_method;
    
    if (status === 'paid') {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Paid
        </Badge>
      );
    }
    
    if (apt.receipt_status === 'pending_verification') {
      return (
        <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Receipt Pending
        </Badge>
      );
    }
    
    if (apt.receipt_status === 'rejected') {
      return (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Receipt Rejected
        </Badge>
      );
    }
    
    if (method && method !== 'stripe' && method !== 'demo') {
      return (
        <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
          <DollarSign className="w-3 h-3" />
          Pay at Service
        </Badge>
      );
    }
    
    if (status === 'pending' || status === 'awaiting') {
      return (
        <Badge className="bg-stone-100 text-stone-800 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Payment Pending
        </Badge>
      );
    }
    
    return null;
  };

  // Check if appointment needs receipt upload
  const needsReceiptUpload = (apt) => {
    const manualMethods = ['cashapp', 'zelle', 'paypal', 'cash', 'pay_at_salon'];
    return manualMethods.includes(apt.payment_method) && 
           apt.payment_status !== 'paid' && 
           !apt.receipt_uploaded;
  };

  // Check if we should show the get started guide
  useEffect(() => {
    if (user && shouldShowGuide('client')) {
      // Delay showing the guide to let dashboard load first
      const timer = setTimeout(() => setShowGetStartedGuide(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      setDashboardError(null);
      const [aptRes, loyaltyRes, rewardsRes, stylistsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/appointments/my`, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => { console.error("Appointments fetch error:", e); return { data: [] }; }),
        axios.get(`${API}/loyalty/points`, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => { console.error("Loyalty fetch error:", e); return { data: { points: 0, tier: { name: 'Bronze', discount: 5 } } }; }),
        axios.get(`${API}/loyalty/rewards`, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => { console.error("Rewards fetch error:", e); return { data: { rewards: [] } }; }),
        axios.get(`${API}/stylists/available`).catch((e) => { console.error("Stylists fetch error:", e); return { data: [] }; }),
        axios.get(`${API}/services/all`).catch((e) => { console.error("Services fetch error:", e); return { data: [] }; })
      ]);
      
      // Safely set state with defaults
      setAppointments(Array.isArray(aptRes.data) ? aptRes.data : []);
      setLoyaltyData({
        points: loyaltyRes.data?.points ?? 0,
        tier: loyaltyRes.data?.tier ?? { name: 'Bronze', discount: 5 }
      });
      setRewards(Array.isArray(rewardsRes.data?.rewards) ? rewardsRes.data.rewards : []);
      setStylists(Array.isArray(stylistsRes.data) ? stylistsRes.data : []);
      setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      setDashboardError("Some features may be unavailable. Please refresh the page.");
      // Set safe defaults
      setAppointments([]);
      setLoyaltyData({ points: 0, tier: { name: 'Bronze', discount: 5 } });
      setRewards([]);
    } finally {
      setLoading(false);
    }
  };

  // Also add fetchDashboardData for review callback
  const fetchDashboardData = fetchData;

  // Fetch payment history
  const fetchPaymentHistory = async () => {
    setLoadingPaymentHistory(true);
    try {
      const response = await axios.get(`${API}/clients/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(response.data.payment_history || []);
      setPaymentSummary(response.data.summary || {
        total_appointments: 0,
        completed_appointments: 0,
        total_spent: 0,
        total_refunded: 0,
        net_spent: 0
      });
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
    } finally {
      setLoadingPaymentHistory(false);
    }
  };

  // Load payment history when tab is selected
  useEffect(() => {
    if (activeTab === "payment-history" && paymentHistory.length === 0) {
      fetchPaymentHistory();
    }
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-emerald-100 text-emerald-800";
      case "pending": return "bg-amber-100 text-amber-800";
      case "completed": return "bg-stone-100 text-stone-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-stone-100 text-stone-800";
    }
  };

  const upcomingAppointments = appointments.filter(apt => 
    apt.status !== "completed" && apt.status !== "cancelled"
  ).slice(0, 5);

  const pastAppointments = appointments.filter(apt => 
    apt.status === "completed" || apt.status === "cancelled"
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <div className="main-with-sidebar">
          <DashboardSkeleton dark={false} />
        </div>
      </div>
    );
  }

  // Guard against missing user data
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center animate-fadeInUp">
          <p className="text-stone-600 mb-4">Session expired or user not found.</p>
          <Button 
            onClick={() => navigate("/")}
            className="btn-primary btn-ripple"
          >
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-mobile-nav">
      {/* Mobile overlay */}
      <div 
        className={`mobile-overlay ${sidebarOpen ? 'mobile-overlay-visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-8 hover:opacity-80 transition-opacity cursor-pointer">
            <div className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center">
              <Scissors className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-xl text-white">MyHairChair</span>
          </Link>

          <nav className="space-y-2">
            <button 
              onClick={() => setActiveTab("overview")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "overview" ? "bg-white/10 text-white" : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Calendar className="w-5 h-5" />
              Overview
            </button>
            <button 
              onClick={() => setActiveTab("appointments")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "appointments" ? "bg-white/10 text-white" : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Clock className="w-5 h-5" />
              Appointments
            </button>
            <button 
              onClick={() => setActiveTab("recurring")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "recurring" ? "bg-white/10 text-white" : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Repeat className="w-5 h-5" />
              Standing Appointments
            </button>
            <button 
              onClick={() => setActiveTab("waitlist")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "waitlist" ? "bg-white/10 text-white" : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Users className="w-5 h-5" />
              Waitlist
            </button>
            <button 
              onClick={() => setActiveTab("favorites")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "favorites" ? "bg-white/10 text-white" : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Heart className="w-5 h-5" />
              My Barbers/Stylists
            </button>
            <button 
              onClick={() => setActiveTab("loyalty")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "loyalty" ? "bg-white/10 text-white" : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Gift className="w-5 h-5" />
              Loyalty & Rewards
            </button>
            
            {/* Referrals Tab */}
            <button 
              onClick={() => setActiveTab("referrals")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "referrals" 
                  ? "bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border border-amber-500/30" 
                  : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
              data-testid="referrals-tab-btn"
            >
              <Trophy className="w-5 h-5" />
              Referrals
              <Badge className="ml-auto bg-amber-500 text-black text-xs">NEW</Badge>
            </button>
            
            {/* Payment History Tab */}
            <button 
              onClick={() => setActiveTab("payment-history")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "payment-history" 
                  ? "bg-white/10 text-white" 
                  : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
              data-testid="payment-history-tab-btn"
            >
              <Receipt className="w-5 h-5" />
              Payment History
            </button>
            
            {/* Reports Tab */}
            <button 
              onClick={() => setActiveTab("reports")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "reports" 
                  ? "bg-white/10 text-white" 
                  : "text-stone-300 hover:bg-white/5 hover:text-white"
              }`}
              data-testid="reports-tab-btn"
            >
              <BarChart3 className="w-5 h-5" />
              Reports
              <Badge className="ml-auto bg-amber-500 text-black text-xs">NEW</Badge>
            </button>
            
            {/* User Guide in Sidebar */}
            <Link 
              to="/user-guide?role=client"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-stone-300 hover:bg-white/5 hover:text-white"
              data-testid="user-guide-sidebar-link"
            >
              <BookOpen className="w-5 h-5" />
              User Guide
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-medium text-white">{user?.name}</div>
              <div className="text-sm text-stone-400">{user?.email}</div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-stone-300 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-with-sidebar" data-testid="client-dashboard">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-stone-200">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <button 
                className="lg:hidden p-2 flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-6 h-6" />
              </button>
              {/* Home Button - Icon only on mobile */}
              <Link 
                to="/" 
                className="flex items-center gap-2 p-2 sm:px-3 sm:py-2 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors group flex-shrink-0"
                data-testid="client-home-btn"
              >
                <Home className="w-4 h-4 text-stone-500 group-hover:text-emerald-700" />
                <span className="text-sm text-stone-500 group-hover:text-emerald-700 hidden sm:inline">Home</span>
              </Link>
              {/* Welcome message - hidden on mobile */}
              <h1 className="font-display text-lg sm:text-2xl font-bold text-emerald-950 hidden sm:block truncate">
                Welcome back, {user?.name?.split(' ')[0]}!
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* User Guide Icon - Header */}
              <Link 
                to="/user-guide?role=client"
                className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors"
                title="User Guide"
                data-testid="user-guide-header-link"
              >
                <BookOpen className="w-5 h-5 text-stone-600" />
              </Link>
              {/* Book button - Icon only on mobile, full text on larger screens */}
              <Button 
                onClick={() => navigate("/book")}
                className="btn-primary rounded-sm px-3 sm:px-4"
                data-testid="book-appointment-btn"
              >
                <Plus className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">Book Appointment</span>
              </Button>
              <ProfileDropdown user={user} />
            </div>
          </div>
        </header>

        <div className="p-6 space-y-6">
          {/* Error Banner */}
          {dashboardError && (
            <div className="bg-amber-100 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="text-amber-600">⚠️</span>
              <span>{dashboardError}</span>
              <button 
                onClick={() => setDashboardError(null)} 
                className="ml-auto text-amber-600 hover:text-amber-800"
              >
                ×
              </button>
            </div>
          )}

          {/* Recurring Appointments Tab */}
          {activeTab === "recurring" && (
            <RecurringAppointments 
              token={token} 
              stylists={stylists}
              services={services}
            />
          )}

          {/* Waitlist Tab */}
          {activeTab === "waitlist" && (
            <WaitlistManager 
              token={token} 
              stylists={stylists}
              services={services}
              isClient={true}
            />
          )}

          {/* My Favorites Tab */}
          {activeTab === "favorites" && (
            <MyFavorites token={token} />
          )}

          {/* Referrals Dashboard */}
          {activeTab === "referrals" && (
            <ReferralDashboard user={user} token={token} />
          )}

          {/* Payment History Tab */}
          {activeTab === "payment-history" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-stone-800">Payment History</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPaymentHistory}
                  disabled={loadingPaymentHistory}
                >
                  {loadingPaymentHistory ? "Loading..." : "Refresh"}
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Total Spent</p>
                    <p className="text-2xl font-bold text-green-600">${paymentSummary.total_spent?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Total Refunded</p>
                    <p className="text-2xl font-bold text-red-500">${paymentSummary.total_refunded?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Net Spent</p>
                    <p className="text-2xl font-bold text-stone-800">${paymentSummary.net_spent?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="text-2xl font-bold text-purple-600">{paymentSummary.completed_appointments || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Payment History List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-purple-500" />
                    Transaction History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingPaymentHistory ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-500">Loading payment history...</p>
                    </div>
                  ) : paymentHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No payment history yet</p>
                      <p className="text-sm text-gray-400 mt-1">Your completed appointments will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentHistory.map((payment, index) => (
                        <div 
                          key={payment.appointment_id || index}
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            payment.refund ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              payment.status === 'completed' ? 'bg-green-100' :
                              payment.status === 'cancelled' ? 'bg-red-100' : 'bg-gray-100'
                            }`}>
                              {payment.status === 'completed' ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : payment.status === 'cancelled' ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                              ) : (
                                <Clock className="w-5 h-5 text-gray-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-stone-800">{payment.service_name}</p>
                              <p className="text-sm text-gray-500">
                                {payment.date} • {payment.stylist_name}
                              </p>
                              {payment.payment_method && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Paid via {payment.payment_method === 'stripe' ? 'Card' : payment.payment_method}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${payment.refund ? 'text-red-600 line-through' : 'text-stone-800'}`}>
                              ${(payment.amount || 0).toFixed(2)}
                            </p>
                            {payment.refund && (
                              <div className="mt-1">
                                <Badge variant="destructive" className="text-xs">
                                  Refunded ${payment.refund.refund_amount?.toFixed(2)}
                                </Badge>
                                <p className="text-xs text-red-600 mt-1">{payment.refund.reason}</p>
                              </div>
                            )}
                            <Badge 
                              variant={payment.status === 'completed' ? 'default' : 'secondary'}
                              className={`mt-1 text-xs ${
                                payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                                payment.status === 'cancelled' ? 'bg-red-100 text-red-800' : ''
                              }`}
                            >
                              {payment.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Help Text */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Have a payment discrepancy?</p>
                      <p className="text-sm text-blue-600 mt-1">
                        If you notice any issues with your payment history, please contact your stylist directly or reach out to our support team.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === "reports" && (
            <div className="space-y-6">
              <Tabs defaultValue="spending" className="space-y-4">
                <TabsList className="bg-amber-500/10 border border-amber-500/30">
                  <TabsTrigger value="spending" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    💰 Spending Summary
                  </TabsTrigger>
                  <TabsTrigger value="loyalty" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    🏆 Loyalty Points
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="spending">
                  <ReportsDashboard 
                    token={token} 
                    reportType="client" 
                    title="Spending Summary"
                  />
                </TabsContent>
                
                <TabsContent value="loyalty">
                  <ReportsDashboard 
                    token={token} 
                    reportType="client_loyalty" 
                    title="Loyalty Points History"
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Overview/Appointments/Loyalty Content */}
          {(activeTab === "overview" || activeTab === "appointments" || activeTab === "loyalty") && (
            <>
              {/* Onboarding Checklist - Non-intrusive */}
              <OnboardingChecklist 
                userRole="client" 
                userData={{ 
                  name: user?.name, 
                  email: user?.email, 
                  appointments: upcomingAppointments 
                }}
              />

              {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
            {/* Loyalty Points Card */}
            <Card className="stat-card group hover:shadow-xl transition-all duration-300" data-testid="loyalty-card">
              <CardContent className="pt-6 relative">
                <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-stone-500 mb-1 font-medium">Loyalty Points</p>
                <p className="text-4xl font-display font-bold text-gradient-amber animate-countUp">
                  {loyaltyData?.points || 0}
                </p>
                <div className={`inline-flex items-center gap-1 mt-3 px-3 py-1 rounded-full text-sm font-medium ${
                  loyaltyData?.tier?.name === 'Platinum' ? 'tier-platinum' :
                  loyaltyData?.tier?.name === 'Gold' ? 'tier-gold' :
                  loyaltyData?.tier?.name === 'Silver' ? 'tier-silver' : 'tier-bronze'
                }`}>
                  <Trophy className="w-3 h-3" />
                  {loyaltyData?.tier?.name || 'Bronze'}
                </div>
                <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {loyaltyData?.tier?.discount ?? 5}% discount on all services
                </p>
              </CardContent>
            </Card>

            {/* Upcoming Appointments Card */}
            <Card className="stat-card group hover:shadow-xl transition-all duration-300" data-testid="appointments-count-card">
              <CardContent className="pt-6 relative">
                <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-stone-500 mb-1 font-medium">Upcoming</p>
                <p className="text-4xl font-display font-bold text-gradient-emerald animate-countUp">
                  {upcomingAppointments.length}
                </p>
                <p className="text-sm text-emerald-600 mt-3">
                  {upcomingAppointments.length > 0 ? 'Appointments scheduled' : 'No upcoming appointments'}
                </p>
              </CardContent>
            </Card>

            {/* Total Visits Card */}
            <Card className="stat-card group hover:shadow-xl transition-all duration-300" data-testid="total-visits-card">
              <CardContent className="pt-6 relative">
                <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm text-stone-500 mb-1 font-medium">Total Visits</p>
                <p className="text-4xl font-display font-bold text-gradient-purple animate-countUp">
                  {pastAppointments.filter(a => a.status === 'completed').length}
                </p>
                <p className="text-sm text-purple-600 mt-3">
                  Completed appointments
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Pending Reviews Banner */}
          <PendingReviews onReviewComplete={fetchDashboardData} />

          {/* Appointments Section */}
          <Card className="card-dashboard" id="appointments">
            <CardHeader>
              <CardTitle className="font-display text-xl">Your Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="upcoming">
                <TabsList className="mb-4">
                  <TabsTrigger value="upcoming" data-testid="upcoming-tab">Upcoming</TabsTrigger>
                  <TabsTrigger value="past" data-testid="past-tab">Past</TabsTrigger>
                </TabsList>

                <TabsContent value="upcoming">
                  {upcomingAppointments.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-600 mb-4">No upcoming appointments</p>
                      <Button onClick={() => navigate("/book")} className="btn-primary rounded-sm">
                        Book Your First Appointment
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {upcomingAppointments.map((apt) => (
                        <div 
                          key={apt.appointment_id}
                          className="p-4 border border-stone-200 rounded-lg hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer"
                          data-testid={`appointment-${apt.appointment_id}`}
                          onClick={() => {
                            setSelectedAppointment(apt);
                            setShowAppointmentDetail(true);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                                <Scissors className="w-6 h-6 text-emerald-950" />
                              </div>
                              <div>
                                <p className="font-medium text-emerald-950">{apt.service_name || apt.service?.name}</p>
                                <p className="text-sm text-stone-600">
                                  with <span className="font-medium">{apt.stylist?.name || apt.stylist_name || "Your Stylist"}</span>
                                </p>
                                <p className="text-sm text-emerald-700 font-medium">
                                  {apt.date} at {apt.time}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className={getStatusColor(apt.status)}>
                                {apt.status}
                              </Badge>
                              <span className="font-medium text-emerald-950">
                                ${(apt.total_amount || apt.price || 0).toFixed(2)}
                              </span>
                              <ChevronRight className="w-5 h-5 text-stone-400" />
                            </div>
                          </div>
                          
                          {/* Payment Status & Actions */}
                          {(apt.payment_method && apt.payment_method !== 'stripe' && apt.payment_method !== 'demo') && (
                            <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2">
                                {getPaymentStatusBadge(apt)}
                                {apt.payment_method && (
                                  <span className="text-xs text-stone-500 capitalize">
                                    via {apt.payment_method === 'pay_at_salon' ? 'Pay at Service' : apt.payment_method}
                                  </span>
                                )}
                              </div>
                              
                              {needsReceiptUpload(apt) && (
                                <Button
                                  size="sm"
                                  className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenReceiptUpload(apt, apt.payment_method);
                                  }}
                                  data-testid={`upload-receipt-btn-${apt.appointment_id}`}
                                >
                                  <Upload className="w-4 h-4 mr-1" />
                                  Upload Receipt
                                </Button>
                              )}
                              
                              {apt.receipt_status === 'pending_verification' && (
                                <span className="text-xs text-amber-600 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Awaiting verification
                                </span>
                              )}
                              
                              {apt.receipt_status === 'rejected' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-300 text-red-600 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenReceiptUpload(apt, apt.payment_method);
                                  }}
                                  data-testid={`reupload-receipt-btn-${apt.appointment_id}`}
                                >
                                  <Upload className="w-4 h-4 mr-1" />
                                  Re-upload
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="past">
                  {pastAppointments.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-600">No past appointments yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pastAppointments.map((apt) => (
                        <div 
                          key={apt.appointment_id}
                          className="flex items-center justify-between p-4 border border-stone-200 rounded-lg"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                              <Scissors className="w-6 h-6 text-stone-400" />
                            </div>
                            <div>
                              <p className="font-medium text-stone-700">{apt.service?.name}</p>
                              <p className="text-sm text-stone-500">
                                with {apt.stylist?.name} • {apt.date}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge className={getStatusColor(apt.status)}>
                              {apt.status}
                            </Badge>
                            {apt.status === 'completed' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedAppointmentForReview(apt);
                                  setShowReviewModal(true);
                                }}
                                data-testid={`review-btn-${apt.appointment_id}`}
                              >
                                <Star className="w-4 h-4 mr-1" />
                                Review
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Rewards Section */}
          <Card className="card-dashboard" id="loyalty">
            <CardHeader>
              <CardTitle className="font-display text-xl">Available Rewards</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rewards.map((reward) => (
                  <div 
                    key={reward.id}
                    className="border border-stone-200 rounded-lg p-4 hover:border-amber-300 transition-colors"
                    data-testid={`reward-${reward.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Gift className="w-8 h-8 text-amber-500" />
                      <span className="text-sm font-medium text-amber-600">
                        {reward.points_required} pts
                      </span>
                    </div>
                    <h3 className="font-medium text-emerald-950 mb-1">{reward.name}</h3>
                    <p className="text-sm text-stone-600 mb-4">{reward.description}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      disabled={(loyaltyData?.points || 0) < reward.points_required}
                    >
                      {(loyaltyData?.points || 0) >= reward.points_required ? 'Redeem' : 'Not enough points'}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </>
          )}
        </div>
      </main>

      {/* Floating AI Chat Button */}
      <button
        className="fab animate-pulse-glow"
        onClick={() => setChatOpen(true)}
        data-testid="ai-chat-fab"
      >
        <MessageSquare className="w-6 h-6" />
      </button>

      {/* AI Chatbot */}
      {chatOpen && <AIChatbot onClose={() => setChatOpen(false)} userRole="client" />}

      {/* Invite Friends Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-lg bg-stone-900 border-stone-700 text-white max-h-[90vh] overflow-y-auto" aria-describedby="invite-dialog-description">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Invite Friends to MyHairChair</DialogTitle>
          </DialogHeader>
          <p id="invite-dialog-description" className="sr-only">Share MyHairChair with your friends and help grow our community</p>
          <BetaInviteSystem user={user} onClose={() => setShowInviteModal(false)} />
        </DialogContent>
      </Dialog>

      {/* Write Review Modal */}
      <WriteReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedAppointmentForReview(null);
        }}
        appointment={selectedAppointmentForReview}
        onSubmit={() => {
          // Refresh appointments after review
          fetchDashboardData();
          toast.success("Review submitted! You earned 10 loyalty points.");
        }}
      />

      {/* Get Started Guide */}
      {showGetStartedGuide && (
        <GetStartedGuide
          userRole="client"
          onComplete={() => setShowGetStartedGuide(false)}
          onSkip={() => setShowGetStartedGuide(false)}
        />
      )}

      {/* Receipt Upload Modal */}
      {selectedAppointmentForPayment && (
        <PayAtServiceReceiptUpload
          isOpen={showReceiptUpload}
          onClose={() => {
            setShowReceiptUpload(false);
            setSelectedAppointmentForPayment(null);
            setStylistPaymentInfo(null);
          }}
          appointmentId={selectedAppointmentForPayment.appointment_id}
          paymentMethod={selectedAppointmentForPayment.selectedPaymentMethod || selectedAppointmentForPayment.payment_method}
          amount={selectedAppointmentForPayment.total_amount}
          stylistPaymentInfo={stylistPaymentInfo}
          onSuccess={() => {
            setShowReceiptUpload(false);
            setSelectedAppointmentForPayment(null);
            setStylistPaymentInfo(null);
            toast.success("Receipt uploaded! Your stylist will verify it shortly.");
            fetchData(); // Refresh appointments
          }}
        />
      )}

      {/* Appointment Detail Modal */}
      <AppointmentDetailModal
        appointment={selectedAppointment}
        isOpen={showAppointmentDetail}
        onClose={() => {
          setShowAppointmentDetail(false);
          setSelectedAppointment(null);
        }}
        onUpdate={() => {
          fetchDashboardData();
          setShowAppointmentDetail(false);
          setSelectedAppointment(null);
        }}
      />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav userRole="client" />
    </div>
  );
};

export default ClientDashboard;
